/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Singleton utility for converting design pixel values (based on 1920x1080)
 * to viewport-relative units (vw/vh) for responsive layout calculations.
 */
export class DimensionScaler {
  private static instance: DimensionScaler;

  private readonly BASE_WIDTH: number = 1920;
  private readonly BASE_HEIGHT: number = 1080;

  private widthRatio: number;
  private heightRatio: number;
  private currentWidth: number;
  private currentHeight: number;

  private constructor(currentWidth: number, currentHeight: number) {
    this.currentWidth = currentWidth;
    this.currentHeight = currentHeight;
    this.widthRatio = currentWidth / this.BASE_WIDTH;
    this.heightRatio = currentHeight / this.BASE_HEIGHT;
  }

  /**
   * Initialize the DimensionScaler with the current viewport dimensions.
   * Must be called once at app startup before any scaling operations.
   */
  public static initialize(currentWidth: number, currentHeight: number): void {
    DimensionScaler.instance = new DimensionScaler(currentWidth, currentHeight);
  }

  public static getInstance(): DimensionScaler {
    if (!DimensionScaler.instance) {
      throw new Error(
        "DimensionScaler not initialized. Call DimensionScaler.initialize() first."
      );
    }
    return DimensionScaler.instance;
  }

  /**
   * Convert a design pixel width to a scaled pixel value for the current viewport.
   */
  public getScaledWidth(
    originalWidth: number,
    roundOff: boolean = true
  ): number {
    const scaled = originalWidth * this.widthRatio;
    return roundOff ? Math.round(scaled) : scaled;
  }

  /**
   * Convert a design pixel height to a scaled pixel value for the current viewport.
   */
  public getScaledHeight(
    originalHeight: number,
    roundOff: boolean = true
  ): number {
    const scaled = originalHeight * this.heightRatio;
    return roundOff ? Math.round(scaled) : scaled;
  }

  /**
   * Scale both width and height from design pixels to the current viewport.
   *
   * @param originalWidth - Width in design pixels (1920 base)
   * @param originalHeight - Height in design pixels (1080 base)
   * @param roundOff - Whether to round the result (default true)
   * @returns Object with scaled width and height
   */
  public getScaledDimensions(
    originalWidth: number,
    originalHeight: number,
    roundOff: boolean = true
  ): { width: number; height: number } {
    const scaledWidth = originalWidth * (this.widthRatio || 1);
    const scaledHeight = originalHeight * (this.heightRatio || 1);
    return {
      width: roundOff ? Math.round(scaledWidth) : scaledWidth,
      height: roundOff ? Math.round(scaledHeight) : scaledHeight
    };
  }

  /**
   * Convert a design pixel width to vw units.
   * @param designPx - Width in design pixels (1920 base)
   * @returns Number representing vw value (e.g., 960 → 50)
   */
  public toVw(designPx: number): number {
    return (designPx / this.BASE_WIDTH) * 100;
  }

  /**
   * Convert a design pixel height to vh units.
   * @param designPx - Height in design pixels (1080 base)
   * @returns Number representing vh value (e.g., 540 → 50)
   */
  public toVh(designPx: number): number {
    return (designPx / this.BASE_HEIGHT) * 100;
  }

  /**
   * Convert a design pixel width to a vw CSS string.
   * @param designPx - Width in design pixels (1920 base)
   * @returns CSS string (e.g., "50vw")
   */
  public toVwString(designPx: number): string {
    return `${this.toVw(designPx)}vw`;
  }

  /**
   * Convert a design pixel height to a vh CSS string.
   * @param designPx - Height in design pixels (1080 base)
   * @returns CSS string (e.g., "50vh")
   */
  public toVhString(designPx: number): string {
    return `${this.toVh(designPx)}vh`;
  }

  public getCurrentWidth(): number {
    return this.currentWidth;
  }

  public getCurrentHeight(): number {
    return this.currentHeight;
  }
}
